const DEFAULT_FALLBACK = "/user/dashboard";

/** Only accept same-app relative paths → blocks open-redirect. */
export function isSafeInternalPath(path: string): boolean {
  if (typeof path !== "string" || path.length === 0 || path.length > 2048)
    return false;
  if (!path.startsWith("/")) return false; // relative to origin
  if (path.startsWith("//")) return false; // protocol-relative → external
  if (path.startsWith("/\\")) return false; // backslash tricks
  if (/[\s\r\n\u0000-\u001F]/.test(path)) return false; // control chars
  if (/^\/+[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) return false; // "http://" style
  return true;
}

/** Builds: /auth/sign-in?redirect=<encoded path> */
export function buildSignInUrl(returnPath: string): string {
  const target = isSafeInternalPath(returnPath) ? returnPath : DEFAULT_FALLBACK;
  return `/auth/sign-in?redirect=${encodeURIComponent(target)}`;
}

/**
 * Reads + validates a redirect query value. Returns fallback if unsafe/missing.
 * NOTE: Next.js auto-decodes single-encoded query values, so `raw` may already
 * be decoded. decodeURIComponent on an already-decoded string is idempotent for
 * well-formed values; malformed `%` sequences throw URIError and are caught
 * here → fallback. Encode exactly once on emit, decode once here.
 */
export function getPostAuthRedirect(
  raw: unknown,
  fallback = DEFAULT_FALLBACK,
): string {
  if (typeof raw !== "string") return fallback;
  try {
    const decoded = decodeURIComponent(raw).trim();
    return isSafeInternalPath(decoded) ? decoded : fallback;
  } catch {
    return fallback; // malformed encoding
  }
}
